/* usePlanCompletion — derive a readiness checklist from the case + plan (iter 96).
 *
 * Single source of truth for both:
 *  - PlanCompletionSidebar (right rail with overall %)
 *  - PreSubmissionChecklist (embedded in Submit-to-doctor card)
 *
 * No schema changes — runs entirely off existing fields. Each section returns
 * one of: 'done' | 'warn' | 'empty'.
 *
 *   done  = fully populated and clinically sensible
 *   warn  = populated but with a sensible-default issue (e.g., bite metrics
 *           are outside the recommended range, retainer recall_months looks
 *           too short, etc.)
 *   empty = nothing entered yet
 *
 * Status is "warn" only when the field is filled out but suspect. It's NOT
 * "warn" for empty optional sections (those return 'empty' silently).
 */
import { useMemo } from "react";


/** Clinical reference ranges — also surfaced in the sidebar quick-reference card. */
export const CLINICAL_REF = {
    overjet_init: [0, 10],   // mm; >6mm = significant protrusion
    overbite_init: [-3, 8],  // mm; <0 = open bite, >4 = deep bite
    overjet_final: [1.5, 3.0], // mm; mockup's blocker range
    overbite_final: [1.0, 2.0],
    ipr_max_per_contact: 0.5,
    ipr_total_flag: 4.0,
    retainer_recall_min_months: 3,
    wear_default_min: 7,
    wear_default_max: 21,
};


function isNum(x) {
    return typeof x === "number" && Number.isFinite(x);
}


export default function usePlanCompletion(c, files) {
    return useMemo(() => {
        const plan = c?.plan || {};
        const filesList = files || c?.files || [];
        const videoFile = filesList.find((f) => f.id === plan.video_file_id);

        /* --- 1. 3rd-party deliverable --- */
        const hasLink = !!(plan.external_link && plan.external_link.trim());
        const hasVideo = !!videoFile;
        const sec_deliverable = {
            id: "sec-deliverable",
            label: "3rd-party deliverable",
            status: hasLink || hasVideo ? "done" : "empty",
            message: hasLink ? "Viewer link added" : hasVideo ? "Walkthrough video uploaded" : "Add a viewer link or upload a walkthrough video",
            blocker: !(hasLink || hasVideo),
        };

        /* --- 2 + 3. Aligners & wear schedule (merged iter 97.1) --- */
        const upper = Number(plan.aligner_count_upper || 0);
        const lower = Number(plan.aligner_count_lower || 0);
        const totalAligners = Math.max(upper, lower);
        const hasCounts = upper > 0 || lower > 0;
        const balanceDelta = hasCounts ? Math.abs(upper - lower) : 0;
        const hasWearDefault = isNum(plan.wear_schedule_default_days) && plan.wear_schedule_default_days > 0;
        const wearOutOfRange = hasWearDefault && (
            plan.wear_schedule_default_days < CLINICAL_REF.wear_default_min ||
            plan.wear_schedule_default_days > CLINICAL_REF.wear_default_max
        );

        let aw_status, aw_message;
        if (!hasCounts && !hasWearDefault) {
            aw_status = "empty";
            aw_message = "Set upper / lower counts + default wear duration";
        } else if (!hasCounts) {
            aw_status = "warn";
            aw_message = "Set upper + lower aligner counts";
        } else if (!hasWearDefault) {
            aw_status = "warn";
            aw_message = `${upper} U / ${lower} L · pick default wear duration`;
        } else if (balanceDelta > 8) {
            aw_status = "warn";
            aw_message = `Arch imbalance (${upper}U vs ${lower}L) · ${plan.wear_schedule_default_days}d cadence`;
        } else if (wearOutOfRange) {
            aw_status = "warn";
            aw_message = `${upper}U / ${lower}L · ${plan.wear_schedule_default_days}d outside ${CLINICAL_REF.wear_default_min}-${CLINICAL_REF.wear_default_max}d range`;
        } else {
            aw_status = "done";
            aw_message = `${upper}U / ${lower}L · ${plan.wear_schedule_default_days}d default${(plan.wear_schedule_overrides || []).length ? ` · ${plan.wear_schedule_overrides.length} override${plan.wear_schedule_overrides.length === 1 ? "" : "s"}` : ""}`;
        }
        const sec_aligners_wear = {
            id: "sec-wear",
            label: "Aligners & wear schedule",
            status: aw_status,
            message: aw_message,
            blocker: !hasCounts || !hasWearDefault,
        };

        /* --- 4. Bite metrics --- */
        const initOJ = isNum(plan.initial_overjet_mm) ? plan.initial_overjet_mm : null;
        const initOB = isNum(plan.initial_overbite_mm) ? plan.initial_overbite_mm : null;
        const finalOJ = isNum(plan.final_overjet_mm) ? plan.final_overjet_mm : null;
        const finalOB = isNum(plan.final_overbite_mm) ? plan.final_overbite_mm : null;
        const iprTotal = isNum(plan.total_ipr_mm) ? plan.total_ipr_mm : null;
        const allMetrics = initOJ !== null && initOB !== null && finalOJ !== null && finalOB !== null;
        const finalOJOutOfRange = finalOJ !== null && (finalOJ < CLINICAL_REF.overjet_final[0] || finalOJ > CLINICAL_REF.overjet_final[1]);
        const finalOBOutOfRange = finalOB !== null && (finalOB < CLINICAL_REF.overbite_final[0] || finalOB > CLINICAL_REF.overbite_final[1]);
        const iprFlagged = iprTotal !== null && iprTotal > CLINICAL_REF.ipr_total_flag;
        const metricsWarnReasons = [];
        if (finalOJOutOfRange) metricsWarnReasons.push(`final overjet ${finalOJ}mm outside ${CLINICAL_REF.overjet_final[0]}–${CLINICAL_REF.overjet_final[1]}mm`);
        if (finalOBOutOfRange) metricsWarnReasons.push(`final overbite ${finalOB}mm outside ${CLINICAL_REF.overbite_final[0]}–${CLINICAL_REF.overbite_final[1]}mm`);
        if (iprFlagged) metricsWarnReasons.push(`total IPR ${iprTotal}mm > ${CLINICAL_REF.ipr_total_flag}mm requires justification`);
        const sec_metrics = {
            id: "sec-metrics",
            label: "Bite metrics",
            status: !allMetrics ? (initOJ !== null || initOB !== null || finalOJ !== null || finalOB !== null ? "warn" : "empty")
                : metricsWarnReasons.length ? "warn"
                : "done",
            message: !allMetrics
                ? (initOJ !== null || initOB !== null || finalOJ !== null || finalOB !== null
                    ? "Some bite metrics missing"
                    : "Add initial + final overjet/overbite")
                : metricsWarnReasons.length
                ? metricsWarnReasons.join(" · ")
                : "All four values entered and within range",
            blocker: !allMetrics,
            warnings: metricsWarnReasons,
        };

        /* --- 5. Protocol (IPR + attachments + planner notes) --- */
        const hasIPRText = !!(plan.proposed_ipr && plan.proposed_ipr.trim());
        const iprEntries = Array.isArray(plan.ipr_entries) ? plan.ipr_entries : [];
        const hasIPRStructured = iprEntries.length > 0;
        const iprOverCap = iprEntries.some((r) => Number(r.amount_mm || 0) > CLINICAL_REF.ipr_max_per_contact);
        const hasAttachmentsText = !!(plan.proposed_attachments && plan.proposed_attachments.trim());
        const hasNotes = !!(plan.planner_notes && plan.planner_notes.trim());
        const protocolFilled = hasIPRText || hasIPRStructured || hasAttachmentsText;
        const overcorrectionWarn = plan.has_overcorrection && !(plan.overcorrection_notes && plan.overcorrection_notes.trim());
        const sec_protocol = {
            id: "sec-protocol",
            label: "Proposed protocol",
            status: !protocolFilled && !hasNotes ? "empty"
                : iprOverCap ? "warn"
                : overcorrectionWarn ? "warn"
                : !hasNotes ? "warn"
                : "done",
            message: !protocolFilled && !hasNotes ? "Document IPR, attachments, or planner rationale"
                : iprOverCap ? "One or more IPR contacts exceed the 0.5mm clinical max"
                : overcorrectionWarn ? "Overcorrection enabled but no rationale captured"
                : !hasNotes ? "Add planner notes / clinical rationale"
                : `Protocol documented${hasIPRStructured ? ` · ${iprEntries.length} IPR contact${iprEntries.length === 1 ? "" : "s"}` : ""}`,
            blocker: !protocolFilled,
        };

        /* --- 6. Per-tooth plan --- */
        const movements = Array.isArray(plan.tooth_movements_plan) ? plan.tooth_movements_plan : [];
        const attachments = Array.isArray(plan.attachments_plan) ? plan.attachments_plan : [];
        const sec_pertooth = {
            id: "sec-pertooth",
            label: "Per-tooth plan",
            status: movements.length > 0 ? "done" : "empty",
            message: movements.length > 0
                ? `${movements.length} tooth${movements.length === 1 ? "" : "s"} authored${attachments.length ? ` · ${attachments.length} attachment${attachments.length === 1 ? "" : "s"}` : ""}`
                : "Author at least one tooth movement",
            blocker: movements.length === 0,
        };

        /* --- 7. Retention --- */
        const hasRetainer = !!(plan.retainer_type && plan.retainer_type.trim());
        const recall = Number(plan.retention_recall_months || 0);
        const retainerRecallWarn = hasRetainer && recall > 0 && recall < CLINICAL_REF.retainer_recall_min_months;
        const sec_retention = {
            id: "sec-retention",
            label: "Retention plan",
            status: !hasRetainer ? "empty"
                : retainerRecallWarn ? "warn"
                : "done",
            message: !hasRetainer ? "Pick a retainer type"
                : retainerRecallWarn ? `Recall ${recall} months < ${CLINICAL_REF.retainer_recall_min_months} months minimum`
                : `${plan.retainer_type}${recall > 0 ? ` · ${recall}mo recall` : ""}`,
            blocker: !hasRetainer,
        };

        /* --- 8. Manufacturing spec — optional at authoring time. --- */
        const spec = c?.manufacturing_spec || {};
        const hasSpec = !!(spec.aligner_sheet_id);
        const sec_mfg = {
            id: "sec-mfg",
            label: "Manufacturing spec",
            status: hasSpec ? "done" : "empty",
            message: hasSpec ? "Sheet selection saved" : "Optional — set before case enters production",
            blocker: false,
        };

        const sections = [
            sec_deliverable, sec_aligners_wear, sec_metrics,
            sec_protocol, sec_pertooth, sec_retention, sec_mfg,
        ];

        const total = sections.length;
        const done = sections.filter((s) => s.status === "done").length;
        const warn = sections.filter((s) => s.status === "warn").length;
        const overallPct = Math.round((done / total) * 100);

        const blockers = sections.filter((s) => s.blocker);
        const warnings = sections.filter((s) => s.status === "warn");

        return {
            sections,
            overallPct,
            done,
            warn,
            blockers,
            warnings,
            isReady: blockers.length === 0,
            totalAligners,
        };
    }, [c, files]);
}
